import { TrendingDown, PhoneOff, MoonStar } from "lucide-react";

const IMGS = {
  technicianPanel:
    "https://images.unsplash.com/photo-1660330589827-da8ab7dd3c02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxIVkFDJTIwdGVjaG5pY2lhbiUyMHdvcmtpbmclMjBoZWF0aW5nJTIwYWlyJTIwY29uZGl0aW9uaW5nfGVufDB8fHx8MTc5MTEyNTExNnww&ixlib=rb-4.1.0&q=85",
  technicianRooftop:
    "https://images.unsplash.com/photo-1705579605238-24a90c8799c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxIVkFDJTIwdGVjaG5pY2lhbiUyMHdvcmtpbmclMjBoZWF0aW5nJTIwYWlyJTIwY29uZGl0aW9uaW5nfGVufDB8fHx8MTc5MTEyNTExNnww&ixlib=rb-4.1.0&q=85",
  dispatchOperator:
    "https://images.unsplash.com/photo-1712159018726-4564d92f3ec2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxkaXNwYXRjaCUyMG9mZmljZSUyMGNhbGwlMjBjZW50ZXIlMjBvcGVyYXRvciUyMGhlYWRzZXQlMjB3b3JrZXJ8ZW58MHx8fHwxNzkxMTI1MTE2fDA&ixlib=rb-4.1.0&q=85",
  dispatchOffice:
    "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxkaXNwYXRjaCUyMG9mZmljZSUyMGNhbGwlMjBjZW50ZXIlMjBvcGVyYXRvciUyMGhlYWRzZXQlMjB3b3JrZXJ8ZW58MHx8fHwxNzkxMTI1MTE2fDA&ixlib=rb-4.1.0&q=85",
};

const baseStats = (stat, label) => [
  {
    icon: PhoneOff,
    stat: "85%",
    label: "of callers who can't reach you never call back",
  },
  {
    icon: MoonStar,
    stat: "27%",
    label: "of service calls come in after business hours",
  },
  { icon: TrendingDown, stat, label },
];

const integrationLine = "Works with your existing phone line — no CRM required";

export const trades = {
  plumbers: {
    slug: "plumbers",
    name: "Plumbers",
    source: "plumbers",
    seo: {
      title: "Backfill for Plumbers — Missed Call Text-Back",
      description:
        "Backfill answers missed plumbing calls in seconds with branded SMS, smart qualification and human dispatch — turning burst-pipe emergencies into booked jobs.",
    },
    hero: {
      eyebrow: "Revenue Recovery for Plumbing Companies",
      lines: [
        { text: "Every missed call" },
        { text: "is a $900 emergency", accent: "$900 emergency" },
        { text: "for somebody else." },
      ],
      subtext:
        "Backfill answers in seconds — a company-branded text, smart qualifying questions, and a human dispatcher when it matters. Burst pipes, water heater failures, and slow web leads become booked jobs. No new hire required.",
      integrationLine,
      image: IMGS.dispatchOperator,
      imageAlt: "Dispatch operator answering a customer call",
      qualifiedLabel: "Qualified: burst pipe · tech en route 11 AM–1 PM",
    },
    stats: baseStats("$650+", "average emergency job walking to a competitor"),
    defaultMissed: 15,
    defaultTicket: 650,
    chat: [
      { from: "backfill", text: "Hi, this is Rapid Rooter Plumbing — sorry we missed your call. How can we help?" },
      { from: "customer", text: "Pipe burst under the kitchen sink, water everywhere!" },
      { from: "backfill", text: "That sounds urgent — have you shut off the main water valve?" },
      { from: "customer", text: "Just did. I need someone today." },
      { from: "backfill", text: "A dispatcher has been looped in. Earliest tech window: today, 11 AM–1 PM. Confirm?" },
    ],
  },

  electricians: {
    slug: "electricians",
    name: "Electricians",
    source: "electricians",
    seo: {
      title: "Backfill for Electricians — Missed Call Text-Back",
      description:
        "Backfill answers missed electrical calls in seconds with branded SMS, smart qualification and human dispatch — turning power outages and panel jobs into booked calls.",
    },
    hero: {
      eyebrow: "Revenue Recovery for Electrical Contractors",
      lines: [
        { text: "Every missed call" },
        { text: "is a $600 service call", accent: "$600 service call" },
        { text: "for somebody else." },
      ],
      subtext:
        "Backfill answers in seconds — a company-branded text, smart qualifying questions, and a human dispatcher when it matters. Power outages, panel upgrades, and slow web leads become booked jobs. No new hire required.",
      integrationLine,
      image: IMGS.technicianPanel,
      imageAlt: "Electrician working on an electrical panel",
      qualifiedLabel: "Qualified: partial power outage · booked 10 AM–12 PM",
    },
    stats: baseStats("$600+", "average service call walking to a competitor"),
    defaultMissed: 10,
    defaultTicket: 450,
    chat: [
      { from: "backfill", text: "Hi, this is BrightSpark Electric — sorry we missed your call. How can we help?" },
      { from: "customer", text: "Half the house lost power and the breaker won't reset!" },
      { from: "backfill", text: "Understood — is anything sparking, smoking, or smelling hot?" },
      { from: "customer", text: "No, just dead outlets in two rooms." },
      { from: "backfill", text: "A dispatcher has been looped in. Earliest tech window: today, 10 AM–12 PM. Confirm?" },
    ],
  },

  roofers: {
    slug: "roofers",
    name: "Roofers",
    source: "roofers",
    seo: {
      title: "Backfill for Roofers — Missed Call Text-Back",
      description:
        "Backfill answers missed roofing calls in seconds with branded SMS, smart qualification and human dispatch — turning storm damage and leak calls into booked jobs.",
    },
    hero: {
      eyebrow: "Revenue Recovery for Roofing Companies",
      lines: [
        { text: "Every missed call" },
        { text: "is a $9,000 reroof", accent: "$9,000 reroof" },
        { text: "for somebody else." },
      ],
      subtext:
        "Backfill answers in seconds — a company-branded text, smart qualifying questions, and a human dispatcher when it matters. Storm damage, leak repairs, and forgotten estimates become booked jobs. No new hire required.",
      integrationLine,
      image: IMGS.technicianRooftop,
      imageAlt: "Roofer working on a rooftop",
      qualifiedLabel: "Qualified: storm damage · inspection booked Thu 9 AM",
    },
    stats: baseStats("$9,000+", "average reroof walking to a competitor"),
    defaultMissed: 8,
    defaultTicket: 4500,
    chat: [
      { from: "backfill", text: "Hi, this is Summit Roofing — sorry we missed your call. How can we help?" },
      { from: "customer", text: "Last night's storm tore shingles off and there's a leak!" },
      { from: "backfill", text: "Sorry to hear that — is water actively coming inside right now?" },
      { from: "customer", text: "Yes, into the attic bedroom." },
      { from: "backfill", text: "A dispatcher has been looped in. Emergency tarp window: today, 3–5 PM. Confirm?" },
    ],
  },

  contractors: {
    slug: "contractors",
    name: "General Contractors",
    source: "contractors",
    seo: {
      title: "Backfill for General Contractors — Missed Call Text-Back",
      description:
        "Backfill answers missed calls in seconds with branded SMS, smart qualification and human dispatch — turning remodel and addition enquiries into signed contracts.",
    },
    hero: {
      eyebrow: "Revenue Recovery for General Contractors",
      lines: [
        { text: "Every missed call" },
        { text: "is a $25,000 remodel", accent: "$25,000 remodel" },
        { text: "for somebody else." },
      ],
      subtext:
        "Backfill answers in seconds — a company-branded text, smart qualifying questions, and a human coordinator when it matters. Kitchen remodels, additions, and slow estimate follow-ups become signed contracts. No new hire required.",
      integrationLine,
      image: IMGS.dispatchOffice,
      imageAlt: "Contractor office coordinating project enquiries",
      qualifiedLabel: "Qualified: kitchen remodel · consult booked Fri 2 PM",
    },
    stats: baseStats("$18,000+", "average remodel walking to a competitor"),
    defaultMissed: 6,
    defaultTicket: 7950,
    ticketMax: 30000,
    ticketStep: 250,
    chat: [
      { from: "backfill", text: "Hi, this is Cornerstone Builders — sorry we missed your call. How can we help?" },
      { from: "customer", text: "We want to remodel our kitchen this spring and need a quote." },
      { from: "backfill", text: "Great project — do you have a budget range and start date in mind?" },
      { from: "customer", text: "Around $40k, hoping to start in April." },
      { from: "backfill", text: "A project coordinator has been looped in. Consultation window: Friday, 2–4 PM. Confirm?" },
    ],
  },
};
